import React, { useCallback, useMemo } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';
import Animated, { useAnimatedStyle, useDerivedValue, withTiming } from 'react-native-reanimated';

import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import { EventInfo, isTeamEvent } from '@/api/events/types/types';
import { CloseIcon } from '@/assets/icons/close';
import { TeamIcon } from '@/components/EventCard';
import { GameTrackerButton } from '@/components/GameTrackerButton';
import { Text } from '@/components/TextComponent';
import { HudTeam } from '@/components/event-hud/EventHudDetails';
import { getTeamColors } from '@/components/event-hud/utils';
import { Box, Row } from '@/components/lib/components';
import { getFinalScoreColumns } from '@/feature/bets-sbk/utils/utils';
import { useFantasyFullScreenPagerTransition } from '@/feature/circular-pager/FantasyFullScreenPagerTransitionProvider';
import { useEvent } from '@/feature/event-details-sbk/hooks/use-event';
import { Event } from '@/feature/event-details-sbk/types';
import { getHudHeight } from '@/feature/event-details-sbk/utils/get-hud-height';
import { useEventSupportsScoreboard } from '@/hooks/use-event-supports-scoreboard';
import { useFormatPeriod } from '@/hooks/use-format-period';
import { useGameTrackerConfig } from '@/hooks/use-game-tracker-config';
import { useEventDetailsCache } from '@/hooks/use-sbk-event-details-cache';
import { RootStackParamList } from '@/navigation/types';
import { common, designSystem } from '@/styles/styles';
import { EventStatus, League } from '@/types/api.generated';
import { isWeb } from '@/utils/constants-platform-specific';
import { gameUtils } from '@/utils/games';
import { useShallow } from 'zustand/react/shallow';

import { convertPickemEventToHudEvent } from './PickemEventHud';

export const EVENT_SHEET_TITLE_HEIGHT = 48;

export const EventSheetTitle = ({
    event,
    scrollOffset,
    isOnlyDedicatedHeader,
}: {
    event: EventInfo;
    isOnlyDedicatedHeader?: boolean;
    scrollOffset: Animated.SharedValue<number>;
}) => {
    const { bottomSheetCircularPagerRef } = useFantasyFullScreenPagerTransition();
    const eventTitle = isOnlyDedicatedHeader ? event.league : gameUtils.createTitleLabel(event);

    const navigation = useNavigation<StackNavigationProp<RootStackParamList, 'FantasyEventsSheets'>>();

    // on web we navigate back to the previous screen
    // on mobile we close the bottom sheet
    const onClose = useCallback(() => {
        if (isWeb) {
            navigation.goBack();
            return;
        }

        bottomSheetCircularPagerRef.current?.close();
    }, [bottomSheetCircularPagerRef, navigation]);

    const eventTime = gameUtils.createTimeLabel(event);
    const showScoreboard = useEventSupportsScoreboard(event);
    const pamEventId = event?.dataFeedSourceIds.find(({ source }) => source === 'PAM')?.id ?? '';
    const fetchPamEvent = pamEventId !== '' && gameUtils.isLive(event);

    const { data: pamEvent } = useEvent(pamEventId, fetchPamEvent);

    const eventDetails = useEventDetailsCache(
        useShallow(state => {
            return state.eventDetails[pamEventId] ?? pamEvent?.event_details;
        })
    );

    const livePeriodLabel = useFormatPeriod({
        event: pamEvent,
        periodName: eventDetails?.period_name,
        gameClock: eventDetails?.game_clock,
        sportSpecific: eventDetails?.sport_specific,
        customSeparator: '\n',
    });

    const finalScores = useMemo(
        () => (pamEvent && eventDetails ? getFinalScoreColumns(eventDetails, pamEvent?.sport)[0] : undefined),
        [eventDetails, pamEvent]
    );

    const hudHeight = pamEvent ? getHudHeight({ eventStatus: pamEvent?.status, isPrimaryHud: true }) : 100;

    const isScrolled = useDerivedValue(() => {
        return scrollOffset.value > hudHeight;
    });

    const hasTeams = isTeamEvent(event);

    const eventTitleStyle = useAnimatedStyle(() => {
        if (!hasTeams) {
            return {};
        }
        return {
            opacity: withTiming(isScrolled.value ? 0 : 1, { duration: 200 }),
            transform: [
                {
                    translateY: withTiming(isScrolled.value ? -TRANSLATE_DISTANCE : 0, { duration: 200 }),
                },
            ],
        };
    });

    const eventDetailsStyle = useAnimatedStyle(() => {
        if (!hasTeams) {
            return {};
        }
        return {
            opacity: withTiming(isScrolled.value ? 1 : 0, { duration: 200 }),
            transform: [
                {
                    translateY: withTiming(isScrolled.value ? 0 : TRANSLATE_DISTANCE, { duration: 200 }),
                },
            ],
        };
    });

    const eventHud = convertPickemEventToHudEvent(event);

    return (
        <Row
            height={EVENT_SHEET_TITLE_HEIGHT}
            paddingHorizontal={'s16'}
            paddingVertical={'s12'}
            justifyContent={'space-between'}
            alignItems={'center'}
        >
            <TouchableOpacity onPress={onClose}>
                <CloseIcon />
            </TouchableOpacity>
            <Animated.View
                style={[
                    common.zIndexNegative1,
                    StyleSheet.absoluteFill,
                    common.justifyCenter,
                    common.alignCenter,
                    eventTitleStyle,
                ]}
            >
                <Text variant={'titleLarge'}>{eventTitle}</Text>
            </Animated.View>
            {pamEvent && showScoreboard && isTeamEvent(event) ? <EventGameTracker pamEvent={pamEvent} /> : null}
            {isTeamEvent(event) ? (
                <Animated.View
                    style={[
                        common.zIndexNegative1,
                        StyleSheet.absoluteFill,
                        common.justifyCenter,
                        common.alignCenter,
                        eventDetailsStyle,
                    ]}
                >
                    <TeamEventDetailedInfoRow
                        awayTeam={eventHud?.awayTeam}
                        homeTeam={eventHud?.homeTeam}
                        league={event.league}
                        eventTime={eventTime}
                        awayScore={finalScores?.awayScore}
                        homeScore={finalScores?.homeScore}
                        isLive={event.status === EventStatus.InProgress}
                        matchDetails={livePeriodLabel}
                    />
                </Animated.View>
            ) : null}
        </Row>
    );
};

const EventGameTracker = ({ pamEvent }: { pamEvent: Event }) => {
    const gameTrackerConfig = useGameTrackerConfig(pamEvent.league, pamEvent.event_details);

    return gameTrackerConfig ? (
        <GameTrackerButton config={gameTrackerConfig} iconColor={designSystem.colors.white} />
    ) : null;
};

const TeamEventDetailedInfoRow = ({
    homeScore,
    awayScore,
    homeTeam,
    awayTeam,
    matchDetails,
    league,
    eventTime,
    isLive,
}: {
    homeScore?: number;
    awayScore?: number;
    homeTeam?: HudTeam;
    awayTeam?: HudTeam;
    eventTime: string;
    league: League;
    isLive?: boolean;
    matchDetails?: string;
}) => {
    const { homePrimary, awayPrimary, homeSecondary, awaySecondary } = getTeamColors(
        awayTeam && homeTeam
            ? {
                  awayTeam,
                  homeTeam,
              }
            : undefined
    );

    return (
        <Row gap={'s12'} alignItems={'center'}>
            <TeamIcon
                width={28}
                height={28}
                icon={awayTeam?.logoUrl || ''}
                league={league}
                primaryColor={awayPrimary}
                secondaryColor={awaySecondary}
            />
            {isLive ? (
                <>
                    <Box width={leagueToScoreWidth[league] || 23} alignItems={'center'}>
                        <Text variant={'titleLarge'}>{awayScore}</Text>
                    </Box>
                    <Box minHeight={32} alignItems={'center'} justifyContent={'center'}>
                        <Text variant={'labelMedium'} color={'red500'} numberOfLines={2} textAlign={'center'}>
                            {matchDetails || 'LIVE'}
                        </Text>
                    </Box>
                    <Box width={leagueToScoreWidth[league] || 23} alignItems={'center'}>
                        <Text variant={'titleLarge'}>{homeScore}</Text>
                    </Box>
                </>
            ) : (
                <Text variant={'titleLarge'}>{eventTime}</Text>
            )}
            <TeamIcon
                width={28}
                height={28}
                icon={homeTeam?.logoUrl || ''}
                league={league}
                primaryColor={homePrimary}
                secondaryColor={homeSecondary}
            />
        </Row>
    );
};

const TRANSLATE_DISTANCE = 24;

const leagueToScoreWidth: { [key in League]?: number } = {
    [League.Nfl]: 23,
    [League.NflFullSeason]: 23,
    [League.Nhl]: 23,
    [League.Cfb]: 23,
    [League.Mlb]: 23,
    [League.Nba]: 31,
    [League.Cbb]: 31,
};
